import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  FaEdit,
  FaDownload,
  FaCog,
  FaTrash,
  FaChevronDown,
} from "react-icons/fa";
import * as XLSX from "xlsx";
import { getData, putData, deleteData } from "../../../services/FetchNodeservices";

/* ================================
   HELPERS
================================ */
const toCamelCase = (str) =>
  str.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

/* ================================
   MODAL
================================ */
function Modal({ open, title, onClose, children }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white w-full max-w-2xl rounded-xl shadow-2xl overflow-hidden">
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button onClick={onClose} className="text-xl">✕</button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

/* ================================
   MAIN
================================ */
export default function AdminCandidateReport() {
  const [rows, setRows] = useState([]);
  const [editModal, setEditModal] = useState(false);
  const [selected, setSelected] = useState(null);

const [columns, setColumns] = useState({
  position: true,
  company_applied: true,
  candidate_name: true,
  phone_number: true,
  email_id: true,
  current_company: true,
  current_designation: true,
  experience: true,
  current_ctc: true,
  notice_period: true,
  reason_for_change: true,
  candidate_status: true,
  added_by: true
});

  const [search, setSearch] = useState("");
  const [companyFilter, setCompanyFilter] = useState([]);
  const [designationFilter, setDesignationFilter] = useState([]);

  const [companyList, setCompanyList] = useState([]);
  const [designationList, setDesignationList] = useState([]);

  const [openCompany, setOpenCompany] = useState(false);
  const [openDesignation, setOpenDesignation] = useState(false);
  const [openColumns, setOpenColumns] = useState(false);

  const companyRef = useRef();
  const designationRef = useRef();
  const columnRef = useRef();

  /* CLOSE DROPDOWNS */
  useEffect(() => {
    const handler = (e) => {
      if (companyRef.current && !companyRef.current.contains(e.target))
        setOpenCompany(false);
      if (designationRef.current && !designationRef.current.contains(e.target))
        setOpenDesignation(false);
      if (columnRef.current && !columnRef.current.contains(e.target))
        setOpenColumns(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* FETCH */
const fetchReport = async () => {
  try {
    const res = await getData("candidate-report/fetch");

    if (res?.status) {
      setRows(res.data || []);
    } else {
      console.error("Fetch failed", res);
    }
  } catch (err) {
    console.error("API Error:", err);
  }
};

  useEffect(() => {
    fetchReport();
  }, []);

  /* FILTER OPTIONS */
  useEffect(() => {
    setCompanyList([...new Set(rows.map((r) => r.company_applied).filter(Boolean))]);
    setDesignationList([...new Set(rows.map((r) => r.position).filter(Boolean))]);
  }, [rows]);

  /* FILTER + SEARCH */
  const filteredRows = useMemo(() => {
    const q = search.toLowerCase();
    return rows.filter((r) => {
      const matchSearch = Object.values(r).some((v) =>
        v?.toString().toLowerCase().includes(q)
      );
      const matchCompany =
        companyFilter.length === 0 || companyFilter.includes(r.company_applied);
      const matchDesignation =
        designationFilter.length === 0 || designationFilter.includes(r.position);
      return matchSearch && matchCompany && matchDesignation;
    });
  }, [rows, search, companyFilter, designationFilter]);

  /* EXPORT */
  const exportExcel = () => {
    const exportData = filteredRows.map((row) => {
      const obj = {};
      Object.keys(columns).forEach((key) => {
        if (columns[key]) obj[toCamelCase(key)] = row[key];
      });
      return obj;
    });
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Candidates");
    XLSX.writeFile(wb, "AdminCandidateReport.xlsx");
  };

  /* EDIT */
  const openEdit = (row) => {
    setSelected({ ...row });
    setEditModal(true);
  };

  const saveUpdate = async () => {
    const res = await putData(
      `candidate-report/update/${selected.candidate_id}`,
      selected
    );
    if (res?.status) {
      fetchReport();
      setEditModal(false);
    }
  };

const deleteCandidate = async (id) => {
  if (!window.confirm("Delete this candidate?")) return;

  try {
    const res = await deleteData(`candidate-report/delete/${id}`);

    if (res?.status) {
      fetchReport();
    } else {
      alert(res?.message || "Delete failed");
    }
  } catch (error) {
    console.error("Delete Error:", error);
  }
};

  return (
    <div className="md:ml-72 px-4 md:px-6 pt-24 pb-8 min-h-screen text-slate-800">

      <h2 className="text-lg md:text-xl font-bold text-center text-indigo-700 mb-4">
        Admin Candidate Report
      </h2>

      {/* FILTER BAR */}
      <div className="glass-card p-3 flex flex-wrap gap-3 items-center mb-4">

        <input
          placeholder="Search..."
          className="glass-input px-3 py-1.5 rounded-xl text-sm outline-none w-full sm:w-64"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {/* COMPANY */}
        <div className="relative" ref={companyRef}>
          <button
            onClick={() => setOpenCompany((p) => !p)}
            className="glass-btn px-3 py-1.5 rounded-xl flex gap-2 text-sm items-center"
          >
            Company ({companyFilter.length}) <FaChevronDown />
          </button>

          {openCompany && (
            <div className="absolute glass-card w-56 mt-1.5 max-h-60 overflow-y-auto z-50 p-2 flex flex-col gap-1">
              {companyList.map((c) => (
                <label key={c} className="flex gap-2 px-3 py-1 text-sm">
                  <input
                    type="checkbox"
                    checked={companyFilter.includes(c)}
                    onChange={() =>
                      setCompanyFilter((p) =>
                        p.includes(c) ? p.filter((x) => x !== c) : [...p, c]
                      )
                    }
                  />
                  {c}
                </label>
              ))}
            </div>
          )}
        </div>

        {/* POSITION */}
        <div className="relative" ref={designationRef}>
          <button
            onClick={() => setOpenDesignation((p) => !p)}
            className="glass-btn px-3 py-1.5 rounded-xl flex gap-2 text-sm items-center"
          >
            Position ({designationFilter.length}) <FaChevronDown />
          </button>

          {openDesignation && (
            <div className="absolute glass-card w-56 mt-1.5 max-h-60 overflow-y-auto z-50 p-2 flex flex-col gap-1">
              {designationList.map((d) => (
                <label key={d} className="flex gap-2 px-3 py-1 text-sm">
                  <input
                    type="checkbox"
                    checked={designationFilter.includes(d)}
                    onChange={() =>
                      setDesignationFilter((p) =>
                        p.includes(d) ? p.filter((x) => x !== d) : [...p, d]
                      )
                    }
                  />
                  {d}
                </label>
              ))}
            </div>
          )}
        </div>

        {/* COLUMNS */}
        <div className="relative sm:ml-auto" ref={columnRef}>
          <button
            onClick={() => setOpenColumns((p) => !p)}
            className="bg-gray-700 text-white px-3 py-1 rounded flex gap-2 text-sm"
          >
            <FaCog /> Columns
          </button>

          {openColumns && (
            <div className="absolute right-0 mt-1 bg-white border rounded shadow-lg w-56 z-50">
              {Object.keys(columns).map((c) => (
                <label key={c} className="flex gap-2 px-3 py-1 text-sm">
                  <input
                    type="checkbox"
                    checked={columns[c]}
                    onChange={() =>
                      setColumns({ ...columns, [c]: !columns[c] })
                    }
                  />
                  {toCamelCase(c)}
                </label>
              ))}
            </div>
          )}
        </div>

        <button
          onClick={exportExcel}
          className="bg-green-600 text-white px-4 py-2 rounded flex gap-2 text-sm"
        >
          <FaDownload /> Export
        </button>
      </div>

      {/* TABLE */}
      <div className="glass-card overflow-x-auto max-h-[65vh]">
        <table className="min-w-[1100px] w-full text-sm whitespace-nowrap">
          <thead className="bg-slate-50/20 dark:bg-slate-800 text-slate-500 dark:text-slate-400 font-semibold text-[11px] uppercase tracking-wider sticky top-0">
            <tr>
              <th className="p-2">S.No</th>
              {Object.keys(columns).map(
                (c) =>
                  columns[c] && (
                    <th key={c} className="p-2 text-left">
                      {toCamelCase(c)}
                    </th>
                  )
              )}
              <th className="p-2">Action</th>
            </tr>
          </thead>

          <tbody>
            {filteredRows.map((r, i) => (
              <tr key={r.candidate_id} className="border-b hover:bg-gray-50">
                <td className="p-2">{i + 1}</td>

                {Object.keys(columns).map(
                  (c) =>
                    columns[c] && (
                      <td key={c} className="p-2">
                       {r[c] || "-"}
                      </td>
                    )
                )}

                <td className="p-2 flex gap-2">
                  <button
                    onClick={() => openEdit(r)}
                    className="bg-indigo-600 text-white px-2 py-1 rounded"
                  >
                    <FaEdit />
                  </button>
                  <button
                    onClick={() => deleteCandidate(r.candidate_id)}
                    className="bg-red-600 text-white px-2 py-1 rounded"
                  >
                    <FaTrash />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* EDIT MODAL */}
      <Modal
        open={editModal}
        title="Edit Candidate"
        onClose={() => setEditModal(false)}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <input
            className="border rounded px-3 py-2"
            placeholder="Qualification"
            value={selected?.qualification || ""}
            onChange={(e) =>
              setSelected({ ...selected, qualification: e.target.value })
            }
          />
          <input
            className="border rounded px-3 py-2"
            placeholder="Current Company"
            value={selected?.current_company || ""}
            onChange={(e) =>
              setSelected({ ...selected, current_company: e.target.value })
            }
          />
          <input
            className="border rounded px-3 py-2"
            placeholder="Current Designation"
            value={selected?.current_designation || ""}
            onChange={(e) =>
              setSelected({ ...selected, current_designation: e.target.value })
            }
          />
          <select
            className="border rounded px-3 py-2 sm:col-span-2"
            value={selected?.candidate_status || "New"}
            onChange={(e) =>
              setSelected({ ...selected, candidate_status: e.target.value })
            }
          >
            {[
              "New",
              "Contacted",
              "Interview Scheduled",
              "Shortlisted",
              "Rejected",
              "Selected",
              "Offer Given",
              "Joined",
            ].map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            className="border px-4 py-2 rounded"
            onClick={() => setEditModal(false)}
          >
            Cancel
          </button>
          <button
            className="bg-blue-600 text-white px-4 py-2 rounded"
            onClick={saveUpdate}
          >
            Save
          </button>
        </div>
      </Modal>
    </div>
  );
}
